import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';

import { TooltipModule } from 'primeng/tooltip';

import { MessageService, ConfirmationService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ClienteService,
  ContratoService,
  PostoService,
  OcorrenciaService,
  AnexoClienteService,
  API_TO_SECTOR,
} from '@core/services/domain-services';
import { NifService } from '@core/services/nif.service';
import {
  EnumService,
  TipoServicoOption,
} from '@shared/api/services/enum.service';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import {
  calcularCobrancaContrato,
  getModeloCobrancaUi,
} from '@shared/utils/contrato-cobranca.util';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { BackButtonComponent } from '@shared/components/back-button/back-button.component';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { PostosListagemComponent } from '@shared/components/postos-listagem/postos-listagem.component';
import {
  Cliente,
  ContratoCliente,
  ContratoClienteApi,
  Posto,
  Ocorrencia,
  AnexoCliente,
  EnumOcorrenciaCriticidade,
} from '@core/models/models';

function fimAposInicio(
  group: AbstractControl,
): { [key: string]: boolean } | null {
  const inicio = group.get('inicioContrato')?.value;
  const fim = group.get('fimContrato')?.value;
  if (!inicio || !fim) return null;
  return new Date(fim) > new Date(inicio) ? null : { fimAnterior: true };
}

function validarModeloCobranca(
  group: AbstractControl,
): { [key: string]: boolean } | null {
  const modelo = group.get('modeloCobranca')?.value;
  if (!modelo) return null;
  if (modelo === 'fixo_mensal') {
    return Number(group.get('valorMensal')?.value) > 0
      ? null
      : { modeloIncompleto: true };
  }
  const valorUnitario = Number(group.get('valorUnitario')?.value) || 0;
  if (valorUnitario <= 0) return { modeloIncompleto: true };
  const qtdCtrl =
    modelo === 'por_efetivo'
      ? group.get('numEfetivos')
      : modelo === 'por_posto'
        ? group.get('numPostos')
        : group.get('numHoras');
  return Number(qtdCtrl?.value) > 0 ? null : { modeloIncompleto: true };
}

const CLIENTE_DETAIL_TABS: Record<string, string> = {
  'visao-geral': '0',
  geral: '0',
  contratos: '1',
  contrato: '1',
  postos: '2',
  posto: '2',
  ocorrencias: '3',
  ocorrencia: '3',
};

export interface ClienteDetalheVM extends Cliente {
  IsActive?: boolean;
}

@Component({
  selector: 'app-detalhe-cliente',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    TabsModule,
    DrawerModule,
    InputTextModule,
    InputNumberModule,
    DatePickerModule,
    SelectModule,
    SkeletonModule,
    TooltipModule,
    EmptyStateComponent,
    BackButtonComponent,
    BreadcrumbComponent,
    StatusBadgeComponent,
    PostosListagemComponent,
  ],
  templateUrl: './detalhe-cliente.component.html',
  styleUrl: './detalhe-cliente.component.scss',
})
export class DetalheClienteComponent implements OnInit {
  @ViewChild('editFileInput') editFileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('uploadFileInput')
  uploadFileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('contratoFileInput')
  contratoFileInputRef!: ElementRef<HTMLInputElement>;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly clienteSvc = inject(ClienteService);
  private readonly contratoSvc = inject(ContratoService);
  private readonly postoSvc = inject(PostoService);
  private readonly ocorrenciaSvc = inject(OcorrenciaService);
  private readonly anexoSvc = inject(AnexoClienteService);
  private readonly enumSvc = inject(EnumService);
  private readonly nifSvc = inject(NifService);
  private readonly msg = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  cliente: ClienteDetalheVM | null = null;
  loadingCliente = true;
  clienteId = 0;
  activeTab = '0';
  private pendingRouteAction: string | null = null;

  showEditDialog = false;
  savingEdit = false;
  editForm!: FormGroup;
  logoPreview: string | null = null;
  logoFile: File | null = null;
  nifSearching = false;

  showContratoDialog = false;
  savingContrato = false;
  editingContrato: ContratoCliente | null = null;
  contratoForm!: FormGroup;
  contratoFile: File | null = null;

  readonly modeloCobrancas = [
    { label: 'Fixo Mensal', value: 'fixo_mensal' },
    { label: 'Por Efetivo', value: 'por_efetivo' },
    { label: 'Por Posto', value: 'por_posto' },
    { label: 'Por Hora', value: 'por_hora' },
  ];
  modeloCobranca: string = 'fixo_mensal';
  tiposServico: TipoServicoOption[] = [];

  get cf(): { [key: string]: AbstractControl } {
    return this.contratoForm?.controls ?? {};
  }

  get contratoDuracaoMeses(): number {
    const inicio = this.contratoForm?.get('inicioContrato')?.value;
    const fim = this.contratoForm?.get('fimContrato')?.value;
    if (!inicio || !fim) return 0;
    const i = new Date(inicio),
      f = new Date(fim);
    if (f <= i) return 0;
    return (
      (f.getFullYear() - i.getFullYear()) * 12 + (f.getMonth() - i.getMonth())
    );
  }

  documentos: AnexoCliente[] = [];
  loadingDocumentos = true;
  showUploadDialog = false;
  uploadDesignacao = '';
  uploadFile: File | null = null;
  uploading = false;

  readonly sectors = [
    { label: 'Banca e Finanças', value: 'banca' },
    { label: 'Petróleo e Gás', value: 'petroleo' },
    { label: 'Aviação e Aeroportos', value: 'aviacao' },
    { label: 'Telecomunicações', value: 'telecomunicacoes' },
    { label: 'Saúde', value: 'saude' },
    { label: 'Comércio e Retalho', value: 'comercio' },
    { label: 'Hotelaria e Turismo', value: 'hotelaria' },
    { label: 'Indústria e Manufatura', value: 'industria' },
    { label: 'Sector Público', value: 'publico' },
    { label: 'Construção', value: 'construcao' },
    { label: 'Outro', value: 'outro' },
  ];

  contratos: ContratoCliente[] = [];
  loadingContratos = true;
  openMenuId: number | null | undefined = null;

  @HostListener('document:click')
  closeMenus(): void {
    this.openMenuId = null;
  }

  _postosKpi: Posto[] = [];

  ocorrencias: Ocorrencia[] = [];
  loadingOcorrencias = true;

  readonly sectorMap: Record<string, string> = {
    banca: 'Banca e Finanças',
    petroleo: 'Petróleo e Gás',
    aviacao: 'Aviação e Aeroportos',
    telecomunicacoes: 'Telecomunicações',
    saude: 'Saúde',
    comercio: 'Comércio e Retalho',
    hotelaria: 'Hotelaria e Turismo',
    industria: 'Indústria e Manufatura',
    publico: 'Sector Público',
    construcao: 'Construção',
    outro: 'Outro',
  };

  ngOnInit(): void {
    this.clienteId = Number(this.route.snapshot.paramMap.get('id'));

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.activeTab = this.resolveTab(params.get('tab'));
        this.pendingRouteAction = this.normalizeRouteToken(params.get('acao'));
        this.applyRouteAction();
        this.cdr.markForCheck();
      });

    this.loadCliente();
    this.loadContratos();
    this.loadPostosKpi();
    this.loadOcorrencias();
    this.loadTiposServico();
  }

  private loadTiposServico(): void {
    this.enumSvc
      .getTipoServico()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (opcoes) => {
          this.tiposServico = opcoes;
          this.cdr.markForCheck();
        },
        error: () => {
          this.tiposServico = [];
        },
      });
  }

  private resolveTab(value: string | null): string {
    const tab = this.normalizeRouteToken(value) || 'visao-geral';
    return CLIENTE_DETAIL_TABS[tab] ?? '0';
  }

  private normalizeRouteToken(value: string | null): string {
    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-');
  }

  private applyRouteAction(): void {
    const action = this.pendingRouteAction;
    if (!action || !this.cliente) return;

    const isCreateAction = ['criar', 'novo', 'adicionar'].includes(action);
    let handled = false;

    if (this.activeTab === '0' && action === 'editar') {
      this.editarCliente();
      handled = true;
    } else if (this.activeTab === '1' && isCreateAction) {
      this.abrirDialogContrato();
      handled = true;
    } else if (this.activeTab === '2' && isCreateAction) {
      this.pendingRouteAction = null;
      this.criarPostoParaCliente({});
      return;
    }

    if (!handled) return;

    this.pendingRouteAction = null;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { acao: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  loadPostosKpi(): void {
    this.postoSvc
      .getAll({ idCliente: this.clienteId, pageSize: 200 } as any)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this._postosKpi = res.data ?? [];
          this.cdr.detectChanges();
        },
        error: () => {
          this._postosKpi = [];
        },
      });
  }

  loadCliente(): void {
    this.loadingCliente = true;
    this.clienteSvc
      .getById(this.clienteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c: any) => {
          const raw = c?.data ?? c;
          this.cliente = raw ? this.mapApiCliente(raw) : null;
          if (raw?.contratos?.length) {
            this.contratos = raw.contratos.map((ct: any) =>
              this.mapApiContrato(ct),
            );
            this.loadingContratos = false;
          }
          this.loadingCliente = false;
          this.applyRouteAction();
          this.cdr.detectChanges();
        },
        error: () => {
          this.cliente = null;
          this.loadingCliente = false;
        },
      });
  }

  private mapApiCliente(c: any): ClienteDetalheVM {
    // Mapeia prioritariamente a variável IsActive enviada pela API
    const isAtivo = c.IsActive !== undefined
      ? c.IsActive
      : (c.isActive !== undefined ? c.isActive : (c.ativo !== undefined ? c.ativo : true));

    return {
      id: c.id,
      nome: c.nome,
      nif: c.nif,
      numTelefone: c.numTelefone,
      nomeResponsavel: c.nomeResponsavel,
      email: c.email,
      endereco: c.endereco,
      website: c.webSite,
      logoUrl: c.url ? `${c.url}?t=${Date.now()}` : undefined,
      IsActive: !!isAtivo,
      ativo: !!isAtivo,
      sector: API_TO_SECTOR[c.setorAtividadeEmpresaEnum] ?? undefined,
    } as any;
  }

  private mapApiContrato(c: ContratoClienteApi): ContratoCliente {
    return {
      id: c.id,
      idCliente: c.idCliente ?? this.clienteId,
      valorMensal: c.valorMensal,
      numPostos: c.numPostos,
      tipoServico: c.tipoServico,
      tipoServicoDescricao: c.tipoServicoDescricao,
      modeloCobranca: c.modeloCobranca,
      modeloCobrancaDescricao: c.modeloCobrancaDescricao,
      valorUnitario: c.valorUnitario,
      quantidade: c.quantidade,
      descricaoFaturacao: c.descricaoFaturacao,
      inicioContrato: c.dataInicioContrato ?? c.inicioContrato ?? '',
      fimContrato: c.dataFimContrato ?? c.fimContrato ?? '',
      dataFaturacao:
        c.diaFaturacao ?? c.dataFaturacaoContrato ?? c.dataFaturacao,
    };
  }

  loadContratos(): void {
    this.loadingContratos = true;
    this.contratoSvc
      .getByCliente(this.clienteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: ContratoClienteApi[] | { data: ContratoClienteApi[] }) => {
          const list = Array.isArray(res) ? res : (res.data ?? []);
          this.contratos = list.map((c) => this.mapApiContrato(c));
          this.loadingContratos = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.contratos = [];
          this.loadingContratos = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadOcorrencias(): void {
    this.loadingOcorrencias = true;
    this.ocorrenciaSvc
      .getAll({ idCliente: this.clienteId } as any)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.ocorrencias = res.data ?? [];
          this.loadingOcorrencias = false;
        },
        error: () => {
          this.ocorrencias = [];
          this.loadingOcorrencias = false;
        },
      });
  }

  loadDocumentos(): void {
    this.loadingDocumentos = true;
    this.anexoSvc
      .getByCliente(this.clienteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (docs) => {
          this.documentos = docs ?? [];
          this.loadingDocumentos = false;
        },
        error: () => {
          this.documentos = [];
          this.loadingDocumentos = false;
        },
      });
  }

  openUploadDialog(): void {
    this.uploadDesignacao = '';
    this.uploadFile = null;
    this.showUploadDialog = true;
  }

  onUploadFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) this.uploadFile = input.files[0];
  }

  openEdit(p: Posto): void {
    if (p.id) this.router.navigate(['/admin/postos', p.id, 'editar']);
  }

  confirmDelete(p: Posto): void {
    this.confirm.confirm({
      header: 'Eliminar Posto',
      message: `O posto "${p.nome}" será eliminado. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {},
    });
  }

  triggerUploadInput(): void {
    this.uploadFileInputRef?.nativeElement.click();
  }

  upload(): void {
    if (!this.uploadDesignacao.trim() || !this.uploadFile) {
      this.msg.add({
        severity: 'warn',
        summary: 'Dados incompletos',
        detail: 'Preencha a designação e seleccione um ficheiro.',
        life: 3000,
      });
      return;
    }
    this.uploading = true;
    const fd = new FormData();
    fd.append('idCliente', String(this.clienteId));
    fd.append('designacao', this.uploadDesignacao.trim());
    fd.append('ficheiro', this.uploadFile);
    this.anexoSvc
      .upload(fd)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (doc) => {
          this.documentos = [doc, ...this.documentos];
          this.msg.add({
            severity: 'success',
            summary: 'Documento anexado',
            detail: doc.designacao,
            life: 3000,
          });
          this.showUploadDialog = false;
          this.uploading = false;
        },
        error: () => {
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao anexar documento.',
            life: 3000,
          });
          this.uploading = false;
        },
      });
  }

  getDocIcon(mime?: string): string {
    if (!mime) return 'ri-file-line';
    if (mime.startsWith('image/')) return 'ri-image-line';
    if (mime === 'application/pdf') return 'ri-file-pdf-2-line';
    if (mime.includes('word')) return 'ri-file-word-2-line';
    if (mime.includes('excel') || mime.includes('spreadsheet'))
      return 'ri-file-excel-2-line';
    return 'ri-file-text-line';
  }

  formatBytes(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  toggleMenu(id: number | undefined, $event: Event): void {
    if (id == null) return;
    $event.stopPropagation();
    this.openMenuId = this.openMenuId === id ? null : id;
  }

  novoContrato(): void {
    this.abrirDialogContrato();
  }

  abrirDialogContrato(): void {
    this.editingContrato = null;
    this.contratoFile = null;
    this.modeloCobranca = 'fixo_mensal';
    this.contratoForm = this.fb.group(
      {
        tipoServico: [null, Validators.required],
        modeloCobranca: ['fixo_mensal', Validators.required],
        valorMensal: [null],
        numPostos: [0],
        valorUnitario: [null],
        numEfetivos: [null],
        numHoras: [null],
        inicioContrato: [null, Validators.required],
        fimContrato: [null, Validators.required],
        dataFaturacao: [null],
      },
      { validators: [fimAposInicio, validarModeloCobranca] },
    );
    this.showContratoDialog = true;
  }

  editarContrato(c: ContratoCliente): void {
    this.editingContrato = c;
    this.contratoFile = null;
    const modelo = getModeloCobrancaUi(c.modeloCobranca);
    this.modeloCobranca = modelo;
    const dataFat = c.dataFaturacao ? new Date(c.dataFaturacao) : null;
    this.contratoForm = this.fb.group(
      {
        tipoServico: [c.tipoServico ?? null, Validators.required],
        modeloCobranca: [modelo, Validators.required],
        valorMensal: [modelo === 'fixo_mensal' ? c.valorMensal : null],
        numPostos: [
          modelo === 'por_posto'
            ? (c.quantidade ?? c.numPostos ?? 0)
            : (c.numPostos ?? 0),
        ],
        valorUnitario: [c.valorUnitario ?? null],
        numEfetivos: [modelo === 'por_efetivo' ? (c.quantidade ?? null) : null],
        numHoras: [modelo === 'por_hora' ? (c.quantidade ?? null) : null],
        inicioContrato: [
          c.inicioContrato ? new Date(c.inicioContrato) : null,
          Validators.required,
        ],
        fimContrato: [
          c.fimContrato ? new Date(c.fimContrato) : null,
          Validators.required,
        ],
        dataFaturacao: [dataFat && dataFat.getFullYear() > 1 ? dataFat : null],
      },
      { validators: [fimAposInicio, validarModeloCobranca] },
    );
    this.showContratoDialog = true;
  }

  saveContrato(): void {
    if (this.contratoForm.invalid) {
      this.contratoForm.markAllAsTouched();
      return;
    }
    this.savingContrato = true;
    const v = this.contratoForm.value;

    const cobranca = calcularCobrancaContrato({
      modelo: v.modeloCobranca,
      valorMensal: v.valorMensal,
      valorUnitario: v.valorUnitario,
      numEfetivos: v.numEfetivos,
      numPostos: v.numPostos,
      numHoras: v.numHoras,
    });

    const formData = new FormData();
    formData.append('IdCliente', String(this.clienteId));
    formData.append('TipoServico', String(v.tipoServico));
    formData.append('ModeloCobranca', String(cobranca.modeloCobranca));
    formData.append('ValorUnitario', String(cobranca.valorUnitario));
    formData.append('Quantidade', String(cobranca.quantidade));
    formData.append('DescricaoFaturacao', cobranca.descricaoFaturacao);
    formData.append('ValorMensal', String(cobranca.valorMensal));
    formData.append('NumPostos', String(v.numPostos ?? 1));
    formData.append('InicioContrato', this.fmtDate(v.inicioContrato));
    formData.append('FimContrato', this.fmtDate(v.fimContrato));

    if (v.dataFaturacao) {
      formData.append('DiaFaturacao', this.fmtDate(v.dataFaturacao));
    }

    if (this.contratoFile) {
      formData.append('Ficheiro', this.contratoFile, this.contratoFile.name);
    }

    const obs = this.editingContrato?.id
      ? this.contratoSvc.updateWithFile(
          this.editingContrato.id,
          formData,
          this.contratoFile,
        )
      : this.contratoSvc.createWithFile(formData, this.contratoFile);

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        const msg = this.editingContrato
          ? 'Contrato actualizado com sucesso.'
          : 'Contrato criado com sucesso.';
        this.msg.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: msg,
          life: 3000,
        });
        this.showContratoDialog = false;
        this.savingContrato = false;
        this.contratoFile = null;
        this.loadContratos();
      },
      error: () => {
        this.msg.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Falha ao guardar contrato.',
          life: 4000,
        });
        this.savingContrato = false;
      },
    });
  }

  triggerContratoFileInput(): void {
    this.contratoFileInputRef?.nativeElement.click();
  }

  onContratoFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) this.contratoFile = input.files[0];
  }

  removeContratoFile($event: Event): void {
    $event.stopPropagation();
    this.contratoFile = null;
    if (this.contratoFileInputRef?.nativeElement)
      this.contratoFileInputRef.nativeElement.value = '';
  }

  eliminarContrato(c: ContratoCliente): void {
    this.confirm.confirm({
      header: 'Eliminar Contrato',
      message: `O Contrato #${c.id} será eliminado permanentemente. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (!c.id) return;
        this.contratoSvc
          .delete(c.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.contratos = this.contratos.filter((x) => x.id !== c.id);
              this.msg.add({
                severity: 'success',
                summary: 'Eliminado',
                detail: `Contrato #${c.id} removido.`,
                life: 3000,
              });
            },
            error: () =>
              this.msg.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Falha ao eliminar contrato.',
                life: 3000,
              }),
          });
      },
    });
  }

  private fmtDate(d: any): string {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return String(d);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  criarPosto(): void {
    this.router.navigate(['/admin/postos/novo'], {
      queryParams: { clienteId: this.clienteId },
    });
  }

  irParaPosto(p: Posto): void {
    if (p.id) this.router.navigate(['/admin/postos', p.id]);
  }

  deleteAnexo(doc: AnexoCliente): void {
    this.confirm.confirm({
      header: 'Eliminar Documento',
      message: `O documento "${doc.designacao}" será eliminado permanentemente. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (!doc.id) return;
        this.anexoSvc
          .delete(doc.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.documentos = this.documentos.filter((d) => d.id !== doc.id);
              this.msg.add({
                severity: 'success',
                summary: 'Eliminado',
                detail: `"${doc.designacao}" removido.`,
                life: 3000,
              });
            },
            error: () =>
              this.msg.add({
                severity: 'error',
                summary: 'Erro',
                detail: 'Falha ao eliminar documento.',
                life: 3000,
              }),
          });
      },
    });
  }

  // ── KPIs ──────────────────────────────────────────────────────────────────────
  get kpiContratosAtivos(): number {
    return this.contratos.filter((c) => this.getContratoEstado(c) === 'ATIVO')
      .length;
  }
  get kpiPostos(): number {
    return this._postosKpi.length;
  }
  get kpiEfetivos(): number {
    return this._postosKpi.reduce((a, p) => a + (p.numEfetivosTurno ?? 0), 0);
  }
  get kpiOcorrenciasAbertas(): number {
    return this.ocorrencias.filter(
      (o) =>
        o.criticidade === EnumOcorrenciaCriticidade.URGENTE ||
        o.criticidade === EnumOcorrenciaCriticidade.ALTA,
    ).length;
  }
  get valorMensalAtual(): number {
    const ativo = this.contratos.find(
      (c) => this.getContratoEstado(c) === 'ATIVO',
    );
    return ativo?.valorMensal ?? 0;
  }

  get totalMensalCalculado(): number {
    const v = this.contratoForm?.value;
    if (!v) return 0;
    const vu = Number(v.valorUnitario) || 0;
    switch (this.modeloCobranca) {
      case 'por_efetivo':
        return vu * (Number(v.numEfetivos) || 0);
      case 'por_posto':
        return vu * (Number(v.numPostos) || 0);
      case 'por_hora':
        return vu * (Number(v.numHoras) || 0);
      default:
        return 0;
    }
  }

  onModeloChange(evt?: any): void {
    this.modeloCobranca = evt?.value ?? 'fixo_mensal';
    this.contratoForm.get('numPostos')?.setValue(0);
    this.contratoForm.get('valorMensal')?.setValue(null);
    this.contratoForm.get('valorUnitario')?.setValue(null);
    this.contratoForm.get('numEfetivos')?.setValue(null);
    this.contratoForm.get('numHoras')?.setValue(null);
  }

  clearLogoUrl(): void {
    if (this.cliente) this.cliente = { ...this.cliente, logoUrl: undefined };
  }

  formatPhone(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 9);
    let formatted = '';
    if (digits.length > 0) formatted = digits.slice(0, 3);
    if (digits.length > 3) formatted += ' ' + digits.slice(3, 6);
    if (digits.length > 6) formatted += ' ' + digits.slice(6, 9);
    input.value = formatted;
    this.editForm
      ?.get('numTelefone')
      ?.setValue(formatted, { emitEvent: false });
  }

  getInitials(nome: string): string {
    return nome
      .split(' ')
      .filter((n) => n.length > 0)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  getSectorLabel(value?: string): string {
    if (!value) return '—';
    return this.sectorMap[value] ?? value;
  }

  getSectorClass(value?: string): string {
    const map: Record<string, string> = {
      banca: 'sc-banca',
      petroleo: 'sc-petroleo',
      aviacao: 'sc-aviacao',
      telecomunicacoes: 'sc-telecom',
      saude: 'sc-saude',
      comercio: 'sc-comercio',
      hotelaria: 'sc-hotelaria',
      industria: 'sc-industria',
      publico: 'sc-publico',
      construcao: 'sc-construcao',
      outro: 'sc-outro',
    };
    return value ? (map[value] ?? 'sc-outro') : '';
  }

  getContratoEstado(c: ContratoCliente): string {
    const hoje = new Date();
    const fim = new Date(c.fimContrato);
    const dias30 = new Date(hoje);
    dias30.setDate(dias30.getDate() + 30);
    if (fim < hoje) return 'EXPIRADO';
    if (fim < dias30) return 'A VENCER';
    return 'ATIVO';
  }

  getContratoClass(c: ContratoCliente): string {
    const e = this.getContratoEstado(c);
    return (
      {
        ATIVO: 'badge-ativo',
        'A VENCER': 'badge-alta',
        EXPIRADO: 'badge-expirado',
      }[e] ?? ''
    );
  }

  getRiscoClass(r?: string): string {
    const map: Record<string, string> = {
      Baixo: 'risco-baixo',
      Moderado: 'risco-mod',
      Alto: 'risco-alto',
      Critico: 'risco-critico',
    };
    return r ? (map[r] ?? '') : '';
  }

  getCritClass(v: any): string {
    const val = String(v ?? '').toUpperCase();
    if (val === 'ALTA' || val === '1') return 'crit-alta';
    if (val === 'MEDIA' || val === '2') return 'crit-media';
    if (val === 'URGENTE' || val === '3') return 'crit-urgente';
    return '';
  }

  getCritLabel(v: any): string {
    const val = String(v ?? '').toUpperCase();
    if (val === 'ALTA' || val === '1') return 'Alta';
    if (val === 'MEDIA' || val === '2') return 'Média';
    if (val === 'URGENTE' || val === '3') return 'Urgente';
    return v || '';
  }

  getTipoLabel(v: any): string {
    const val = String(v ?? '').toUpperCase();
    const m: Record<string, string> = {
      INTRUSAO: 'Intrusão',
      AUSENCIA: 'Ausência',
      AVARIA: 'Avaria',
      RONDA_INCOMPLETA: 'Ronda Incompleta',
      SUSPEITO: 'Suspeito',
      ACIDENTE: 'Acidente',
      OUTRO: 'Outro',
      '1': 'Incidente',
      '2': 'Falha de Equipamento',
      '3': 'Intrusão',
      '4': 'Emergência',
      '5': 'Outro',
    };
    return m[val] ?? v;
  }

  formatKz(v?: number): string {
    if (v == null) return '—';
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v) +
      ' Kz'
    );
  }

  formatDate(d?: string): string {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-AO');
  }

  getValorAnualContrato(c: ContratoCliente): number {
    return (Number(c.valorMensal) || 0) * 12;
  }

  getContratoFormula(c: ContratoCliente): string {
    const valorMensal = this.formatKz(c.valorMensal);
    if (!c.modeloCobranca) {
      return `Valor mensal registado = ${valorMensal}/mês`;
    }
    if (c.modeloCobranca === 1) {
      return `Mensalidade fixa acordada = ${valorMensal}/mês`;
    }
    if (!c.valorUnitario || !c.quantidade) {
      return `Valor mensal registado = ${valorMensal}/mês`;
    }

    const valorUnitario = this.formatKz(c.valorUnitario);
    const quantidade = this.formatQuantidade(c.quantidade);
    switch (c.modeloCobranca) {
      case 2:
        return `${valorUnitario} × ${quantidade} efectivo(s) = ${valorMensal}/mês`;
      case 3:
        return `${valorUnitario} × ${quantidade} posto(s) = ${valorMensal}/mês`;
      case 4:
        return `${valorUnitario} × ${quantidade} h/mês = ${valorMensal}/mês`;
      default:
        return `Valor mensal registado = ${valorMensal}/mês`;
    }
  }

  getContratoDetalhes(
    c: ContratoCliente,
  ): { label: string; value: string }[] {
    switch (c.modeloCobranca) {
      case 2:
        return [
          {
            label: 'Nº de Efectivos',
            value: this.formatQuantidade(c.quantidade),
          },
          {
            label: 'Valor por Efectivo',
            value: this.formatKz(c.valorUnitario),
          },
        ];
      case 3:
        return [
          { label: 'Nº de Postos', value: this.formatQuantidade(c.quantidade) },
          { label: 'Valor por Posto', value: this.formatKz(c.valorUnitario) },
        ];
      case 4:
        return [
          {
            label: 'Horas / Mês',
            value: `${this.formatQuantidade(c.quantidade)} h`,
          },
          { label: 'Valor por Hora', value: this.formatKz(c.valorUnitario) },
        ];
      default:
        return c.numPostos != null
          ? [{ label: 'Nº de Postos', value: String(c.numPostos) }]
          : [];
    }
  }

  private formatQuantidade(v?: number): string {
    if (v == null) return '—';
    return new Intl.NumberFormat('pt-AO', {
      maximumFractionDigits: 2,
    }).format(v);
  }

  editarCliente(): void {
    if (!this.cliente) return;
    this.logoPreview = this.cliente.logoUrl ?? null;
    this.logoFile = null;
    this.editForm = this.fb.group({
      nome: [this.cliente.nome, Validators.required],
      nif: [this.cliente.nif ?? ''],
      email: [this.cliente.email ?? ''],
      sector: [this.cliente.sector ?? null],
      numTelefone: [this.cliente.numTelefone ?? ''],
      nomeResponsavel: [this.cliente.nomeResponsavel ?? ''],
      endereco: [this.cliente.endereco ?? ''],
      website: [(this.cliente as any).website ?? ''],
    });
    this.showEditDialog = true;
  }

  saveEdit(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.savingEdit = true;
    const fv = this.editForm.value;

    const sectorMap: Record<string, number> = {
      banca: 1,
      petroleo: 2,
      aviacao: 3,
      telecomunicacoes: 4,
      saude: 5,
      comercio: 6,
      hotelaria: 7,
      industria: 8,
      publico: 9,
      construcao: 10,
      outro: 11,
    };

    const sectorNumVal = fv.sector ? sectorMap[fv.sector] ?? 11 : null;

    const formData = new FormData();
    formData.append('Nome', fv.nome ?? '');
    formData.append('Nif', fv.nif ?? '');
    formData.append('Bi', '');
    formData.append('NumTelefone', fv.numTelefone ?? '');
    formData.append('NomeResponsavel', fv.nomeResponsavel ?? '');
    formData.append('WebSite', fv.website ?? '');
    formData.append('Email', fv.email ?? '');
    formData.append('Endereco', fv.endereco ?? '');

    if (sectorNumVal !== null) {
      formData.append('SetorAtividadeEmpresaEnum', String(sectorNumVal));
    }

    if (this.logoFile) {
      formData.append('Fotografia', this.logoFile, this.logoFile.name);
    }

    const temLogo = !!this.logoFile;

    this.clienteSvc
      .updateWithFormData(this.clienteId, formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loadCliente();

          this.msg.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: temLogo
              ? 'Cliente e fotografia actualizados com sucesso.'
              : 'Cliente actualizado com sucesso.',
            life: 3000,
          });

          this.showEditDialog = false;
          this.savingEdit = false;
          this.logoFile = null;
        },
        error: () => {
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Falha ao guardar alterações.',
            life: 3000,
          });

          this.savingEdit = false;
        },
      });
  }

  triggerLogoInput(): void {
    this.editFileInputRef?.nativeElement.click();
  }

  onLogoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.[0]) return;

    const file = input.files[0];

    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.msg.add({
        severity: 'warn',
        summary: 'Formato não suportado',
        detail: 'Apenas imagens PNG ou JPG são aceites.',
        life: 4000,
      });
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.logoPreview = e.target?.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);

    compressImageIfNeeded(file).then((compressed) => {
      this.logoFile = compressed;
      this.cdr.detectChanges();
    });
  }

  searchNif(): void {
    const nif = this.editForm.get('nif')?.value?.trim();
    if (!nif) return;
    this.nifSearching = true;
    this.nifSvc
      .lookup(nif)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c) => {
          this.editForm.patchValue({ nome: c.nome });
          this.msg.add({
            severity: 'success',
            summary: 'NIF encontrado',
            detail: c.nome,
            life: 3000,
          });
          this.nifSearching = false;
        },
        error: () => {
          this.msg.add({
            severity: 'info',
            summary: 'NIF não encontrado',
            detail: 'Preencha os dados manualmente.',
            life: 3000,
          });
          this.nifSearching = false;
        },
      });
  }

  navegarParaPosto(posto: Posto): void {
    if (posto.id) this.router.navigate(['/admin/postos', posto.id]);
  }

  criarPostoParaCliente(evt: { provincia?: string }): void {
    this.router.navigate(['/admin/postos/novo'], {
      queryParams: {
        clienteId: this.clienteId,
        ...(evt.provincia ? { provincia: evt.provincia } : {}),
      },
    });
  }
}